import {
  Breadcrumbs,
  Button,
  Inline,
  Skeleton,
  Stack,
  Typography,
} from '@omnipresentgroup/design-system';
import { useContractor } from '@omnipresentgroup/domains';
import {
  ContractorBillingDetails,
  ContractorContractingDetails,
  ContractorPersonalDetails,
  ContractorStatus,
} from '@omnipresentgroup/features';
import { ContractorState } from '@omnipresentgroup/types';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { CONTRACTORS_PATH } from '../../../constants';
import { ContractorDeactivationModal } from '../../../features/contractor-deactivation';
import { ContractorLiabilityShieldDetails } from '../../../features/contractor-liability-shield';
import { CONTRACTOR_LIST_PAGE_TITLE } from '../ContractorsList';

export const CONTRACTOR_DETAILS_PAGE_TITLE = 'Contractor details';

export const ContractorDetailsPage = () => {
  const navigate = useNavigate();

  const { contractorId } = useParams<{ contractorId: string }>();

  const { data: contractor, isLoading } = useContractor({ id: contractorId! });

  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);

  const PageHeader = () => {
    const subtitle = contractor
      ? `${contractor.residenceCountry.flag ?? ''} ${
          contractor.residenceCountry.name ?? ''
        } | ${contractor.contracts?.[0]?.jobRole}`
      : '';

    return (
      <Stack gap="0">
        <Inline justify="space-between" minH={40} align="center">
          <Inline gap="8" align="center">
            <Typography as="h2" hideParagraphSpacing size="24" weight="medium">
              {isLoading ? (
                <Skeleton width={150} />
              ) : (
                `${contractor?.firstName} ${contractor?.lastName}`
              )}
            </Typography>
            {contractor && <ContractorStatus status={contractor.state} />}
          </Inline>
          {isLoading ? (
            <Skeleton width={105} height={40} />
          ) : (
            <Button
              id="deactivate-contractor-button"
              variant="secondary"
              onClick={() => setDeactivateModalOpen(true)}
              disabled={contractor?.state === ContractorState.INACTIVE}
            >
              Deactivate
            </Button>
          )}
        </Inline>
        <Typography as="span" size="14" color="subtle">
          {isLoading ? <Skeleton width={300} /> : subtitle}
        </Typography>
      </Stack>
    );
  };

  return (
    <>
      <Stack gap="16">
        <Breadcrumbs
          crumbs={[
            { id: 'home', title: 'Home', onClick: () => navigate('/') },
            {
              id: 'contractors',
              title: CONTRACTOR_LIST_PAGE_TITLE,
              onClick: () => navigate(CONTRACTORS_PATH),
            },
            {
              id: 'contractorName',
              title: `${CONTRACTOR_DETAILS_PAGE_TITLE} ${
                contractor
                  ? `| ${contractor?.firstName} ${contractor?.lastName}`
                  : ''
              }`,
              active: true,
            },
          ]}
        />
        <PageHeader />
      </Stack>
      {contractorId && (
        <>
          <ContractorPersonalDetails contractorId={contractorId} />
          <ContractorLiabilityShieldDetails contractorId={contractorId} />
          <ContractorContractingDetails contractorId={contractorId} showEdit />
          <ContractorBillingDetails contractorId={contractorId} />
          {deactivateModalOpen && (
            <ContractorDeactivationModal
              modalProps={{
                open: deactivateModalOpen,
                onOpenChange: setDeactivateModalOpen,
              }}
              handleCancel={() => {
                setDeactivateModalOpen(false);
              }}
              handleSuccess={() => {
                setDeactivateModalOpen(false);
              }}
              contractorId={contractorId}
            />
          )}
        </>
      )}
    </>
  );
};
